import { useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, descargar } from "../lib/api";
import { creditos, fecha, iso } from "../lib/formato";
import {
  Aviso,
  Cabecera,
  Campo,
  Cargando,
  Etiqueta,
  Modal,
  Panel,
  TarjetaDato,
  ThOrden,
  Vacio,
} from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface Gasto {
  id: string;
  numero: number;
  fecha: string;
  concepto: string;
  categoria: string;
  categoriaNombre: string;
  importeCentimos: number;
  proveedor: string | null;
  metodoPago: string;
  notas: string | null;
  anulado: boolean;
  motivoAnulacion: string | null;
  compraNumero: number | null;
  puesto: { id: string; nombre: string } | null;
  usuario: { nombre: string } | null;
}

interface Categoria {
  id: string;
  clave: string;
  nombre: string;
  activa: boolean;
  gastos?: number;
}

interface Respuesta {
  gastos: Gasto[];
  totalCentimos: number;
  porCategoria: Record<string, number>;
  porMetodo: Record<string, number>;
  categorias: Categoria[];
}

/*
 * Las cuatro formas de pagar un gasto. No vale reutilizar las del mostrador:
 * alli hay CARTERA, que es el saldo de un socio, y un gasto de la asociacion
 * no se paga con el monedero de nadie. Ofrecerla en el filtro era ofrecer algo
 * que el servidor rechaza.
 */
const METODOS = ["EFECTIVO", "TARJETA", "TRANSFERENCIA", "DOMICILIADO"];

type Columna = "numero" | "fecha" | "concepto" | "categoria" | "metodo" | "puesto" | "importe";

/**
 * Lo que sale de la asociación: el alquiler, la luz, el agua, las nóminas.
 *
 * La pantalla se parece a un extracto del banco a propósito. Quien la abre
 * suele venir con una pregunta concreta —«¿pagamos ya la luz de marzo?»,
 * «¿cuánto nos costó el año pasado el seguro?»— y para eso hace falta buscar
 * por texto y ordenar por columna, no leer una lista de arriba abajo.
 */
export default function Gastos({ pestanas }: { pestanas?: ReactNode }) {
  const { t, cuenta } = useIdioma();
  const { puede } = useSesion();
  const clienteConsultas = useQueryClient();
  const primeroDeMes = new Date();
  primeroDeMes.setDate(1);

  const [desde, setDesde] = useState(iso(primeroDeMes));
  const [hasta, setHasta] = useState(iso(new Date()));
  const [categoria, setCategoria] = useState("");
  const [metodoPago, setMetodoPago] = useState("");
  const [puestoId, setPuestoId] = useState("");
  const [buscar, setBuscar] = useState("");
  const [incluirCompras, setIncluirCompras] = useState(true);
  const [incluirAnulados, setIncluirAnulados] = useState(false);
  const [orden, setOrden] = useState<Columna>("fecha");
  const [descendente, setDescendente] = useState(true);
  const [editando, setEditando] = useState<Gasto | "nuevo" | null>(null);
  const [anulando, setAnulando] = useState<Gasto | null>(null);
  const [verCategorias, setVerCategorias] = useState(false);
  const [error, setError] = useState("");

  const datos = useQuery({
    queryKey: ["gastos", desde, hasta, categoria, metodoPago, puestoId, buscar, incluirCompras, incluirAnulados],
    queryFn: () =>
      api<Respuesta>("/gastos", {
        query: {
          desde,
          hasta,
          categoria: categoria || undefined,
          metodoPago: metodoPago || undefined,
          puestoId: puestoId || undefined,
          buscar: buscar.trim() || undefined,
          incluirCompras: incluirCompras ? undefined : "false",
          incluirAnulados: incluirAnulados ? "true" : undefined,
        },
      }),
  });

  // Los puestos solo se piden si el club los usa; si no, la columna sobra.
  const puestos = useQuery({
    queryKey: ["puestos"],
    queryFn: () => api<{ id: string; nombre: string }[]>("/club/puestos"),
    enabled: puede("ajustesClub"),
  });
  const hayPuestos = (puestos.data?.length ?? 0) > 0;

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ["gastos"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["caja-actual"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["informe-economico"] });
  };

  const nombreDe = (clave: string) =>
    datos.data?.categorias.find((c) => c.clave === clave)?.nombre ?? clave;

  const categoriasOrdenadas = Object.entries(datos.data?.porCategoria ?? {}).sort(([, a], [, b]) => b - a);
  const mayor = categoriasOrdenadas[0]?.[1] ?? 1;

  const ordenarPor = (c: Columna) => {
    if (c === orden) setDescendente((d) => !d);
    else {
      setOrden(c);
      setDescendente(c === "importe" || c === "fecha" || c === "numero");
    }
  };

  const filas = useMemo(() => {
    const lista = [...(datos.data?.gastos ?? [])];
    const valor = (g: Gasto) => {
      switch (orden) {
        case "numero":
          return g.numero;
        case "concepto":
          return g.concepto.toLowerCase();
        case "categoria":
          return g.categoriaNombre.toLowerCase();
        case "metodo":
          // Se ordena por como se lee en pantalla, no por la clave del servidor.
          return t(`pago.${g.metodoPago}` as Clave);
        case "puesto":
          return g.puesto?.nombre.toLowerCase() ?? "";
        case "importe":
          return g.importeCentimos;
        default:
          return g.fecha;
      }
    };
    lista.sort((a, b) => {
      const x = valor(a);
      const y = valor(b);
      const cmp = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "es");
      return descendente ? -cmp : cmp;
    });
    return lista;
  }, [datos.data, orden, descendente]);

  const filtrado = Boolean(categoria || metodoPago || puestoId || buscar.trim() || !incluirCompras);

  return (
    <>
      <Cabecera titulo={t("ga.titulo")} descripcion={t("ga.descripcion")}>
        {pestanas}
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ width: 160 }} />
        <span className="suave">{t("ga.a")}</span>
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ width: 160 }} />
        {puede("ajustesClub") && (
          <button className="boton secundario" onClick={() => setVerCategorias(true)}>
            {t("ga.categorias")}
          </button>
        )}
        {puede("verInformes") && (
          <button
            className="boton secundario"
            onClick={() =>
              descargar(
                `/exportar/gastos.csv?desde=${desde}&hasta=${hasta}${incluirAnulados ? "&incluirAnulados=true" : ""}`,
                `gastos-${desde}-a-${hasta}.csv`,
              ).catch((fallo) => setError(fallo instanceof Error ? fallo.message : t("ga.falloExportar")))
            }
          >
            {t("ga.exportar")}
          </button>
        )}
        {puede("gestionarGastos") && (
          <button className="boton" onClick={() => setEditando("nuevo")}>
            {t("ga.nuevo")}
          </button>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      <div className="rejilla tres" style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta={filtrado ? t("ga.totalFiltrado") : t("ga.totalPeriodo")}
          valor={datos.data ? creditos(datos.data.totalCentimos) : "—"}
          pie={
            datos.data
              ? cuenta(filas.filter((g) => !g.anulado).length, "ga.unApunte", "ga.apuntes")
              : undefined
          }
        />
        <TarjetaDato
          etiqueta={t("ga.enEfectivo")}
          valor={datos.data ? creditos(datos.data.porMetodo.EFECTIVO ?? 0) : "—"}
          pie={t("ga.saleDeCaja")}
        />
        <TarjetaDato
          etiqueta={t("ga.categoriaMayor")}
          valor={categoriasOrdenadas[0] ? nombreDe(categoriasOrdenadas[0][0]) : "—"}
          pie={categoriasOrdenadas[0] ? creditos(categoriasOrdenadas[0][1]) : undefined}
        />
      </div>

      <div className="rejilla gastos">
        <Panel titulo={t("ga.porCategoria")}>
          {categoriasOrdenadas.length === 0 ? (
            <Vacio>{t("ga.sinGastos")}</Vacio>
          ) : (
            <div className="columna" style={{ gap: 12 }}>
              {categoriasOrdenadas.map(([clave, importe]) => (
                <div key={clave}>
                  <div className="fila separada pequeno" style={{ marginBottom: 4 }}>
                    <button
                      className="boton fantasma"
                      style={{ padding: 0 }}
                      onClick={() => setCategoria(categoria === clave ? "" : clave)}
                    >
                      {nombreDe(clave)}
                    </button>
                    <span className="tabular">{creditos(importe)}</span>
                  </div>
                  <div className="barra">
                    <span style={{ width: `${(importe / mayor) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          titulo={categoria ? t("ga.gastosDe", { categoria: nombreDe(categoria) }) : t("ga.todosLosGastos")}
          sinCuerpo
        >
          <div className="filtros-gastos">
            <input
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              placeholder={t("ga.buscar")}
            />
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">{t("ga.todasCategorias")}</option>
              {(datos.data?.categorias ?? []).map((c) => (
                <option key={c.clave} value={c.clave}>
                  {c.nombre}
                  {c.activa ? "" : t("ga.apagada")}
                </option>
              ))}
            </select>
            <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
              <option value="">{t("ga.cualquierForma")}</option>
              {METODOS.map((m) => (
                <option key={m} value={m}>
                  {t(`pago.${m}` as Clave)}
                </option>
              ))}
            </select>
            {hayPuestos && (
              <select value={puestoId} onChange={(e) => setPuestoId(e.target.value)}>
                <option value="">{t("ga.cualquierPuesto")}</option>
                {(puestos.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            )}
            <label className="casilla">
              <input
                type="checkbox"
                checked={incluirCompras}
                onChange={(e) => setIncluirCompras(e.target.checked)}
              />
              <span>{t("ga.conPagosProveedor")}</span>
            </label>
            <label className="casilla">
              <input
                type="checkbox"
                checked={incluirAnulados}
                onChange={(e) => setIncluirAnulados(e.target.checked)}
              />
              <span>{t("ga.verAnulados")}</span>
            </label>
          </div>

          {datos.isLoading ? (
            <Cargando />
          ) : filas.length === 0 ? (
            <Vacio>
              {filtrado ? t("ga.sinCoincidencias") : t("ga.sinApuntes")}
            </Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <ThOrden columna="numero" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                      {t("ga.numero")}
                    </ThOrden>
                    <ThOrden columna="fecha" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                      {t("ga.fecha")}
                    </ThOrden>
                    <ThOrden columna="concepto" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                      {t("ga.concepto")}
                    </ThOrden>
                    <ThOrden columna="categoria" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                      {t("ga.categoria")}
                    </ThOrden>
                    <ThOrden columna="metodo" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                      {t("ga.pago")}
                    </ThOrden>
                    {hayPuestos && (
                      <ThOrden columna="puesto" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                        {t("ga.puesto")}
                      </ThOrden>
                    )}
                    <ThOrden columna="importe" orden={orden} descendente={descendente} alOrdenar={ordenarPor} num>
                      {t("ga.importe")}
                    </ThOrden>
                    {puede("gestionarGastos") && <th />}
                  </tr>
                </thead>
                <tbody>
                  {filas.map((g) => (
                    <tr key={g.id} className={g.anulado ? "anulada" : undefined}>
                      <td className="suave pequeno tabular">#{g.numero}</td>
                      <td className="pequeno">{fecha(g.fecha)}</td>
                      <td>
                        <strong>{g.concepto}</strong>
                        <div className="suave pequeno">
                          {g.proveedor ? `${g.proveedor} · ` : ""}
                          {g.usuario?.nombre ?? "—"}
                          {g.compraNumero !== null && t("ga.compraNumero", { numero: g.compraNumero })}
                        </div>
                        {g.anulado && (
                          <div className="suave pequeno">
                            {g.motivoAnulacion
                              ? t("ga.anuladoPor", { motivo: g.motivoAnulacion })
                              : t("ga.anulado")}
                          </div>
                        )}
                      </td>
                      <td>
                        <Etiqueta>{g.categoriaNombre}</Etiqueta>
                      </td>
                      <td className="pequeno">{t(`pago.${g.metodoPago}` as Clave)}</td>
                      {hayPuestos && <td className="pequeno">{g.puesto?.nombre ?? "—"}</td>}
                      <td className="num tabular">{creditos(g.importeCentimos)}</td>
                      {puede("gestionarGastos") && (
                        <td>
                          {!g.anulado && (
                            <div className="fila" style={{ gap: 2, justifyContent: "flex-end" }}>
                              {!g.compraNumero && (
                                <button className="boton fantasma" onClick={() => setEditando(g)}>
                                  {t("ga.editar")}
                                </button>
                              )}
                              <button className="boton fantasma" onClick={() => setAnulando(g)}>
                                {t("ga.anular")}
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {editando && (
        <DialogoGasto
          gasto={editando === "nuevo" ? null : editando}
          categorias={(datos.data?.categorias ?? []).filter((c) => c.activa)}
          alCerrar={() => setEditando(null)}
          alFallar={setError}
          alHecho={() => {
            setEditando(null);
            refrescar();
          }}
        />
      )}

      {anulando && (
        <DialogoAnular
          gasto={anulando}
          alCerrar={() => setAnulando(null)}
          alFallar={setError}
          alHecho={() => {
            setAnulando(null);
            refrescar();
          }}
        />
      )}

      {verCategorias && (
        <DialogoCategorias
          alCerrar={() => setVerCategorias(false)}
          alCambiar={() => void clienteConsultas.invalidateQueries({ queryKey: ["gastos"] })}
        />
      )}
    </>
  );
}

function DialogoGasto({
  gasto,
  categorias,
  alCerrar,
  alHecho,
  alFallar,
}: {
  gasto: Gasto | null;
  categorias: Categoria[];
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const [datos, setDatos] = useState({
    fecha: gasto?.fecha.slice(0, 10) ?? iso(new Date()),
    concepto: gasto?.concepto ?? "",
    categoria: gasto?.categoria ?? categorias[0]?.clave ?? "OTROS",
    importeEuros: gasto ? String(gasto.importeCentimos / 100) : "",
    proveedor: gasto?.proveedor ?? "",
    metodoPago: gasto?.metodoPago ?? "EFECTIVO",
    notas: gasto?.notas ?? "",
  });
  const { t } = useIdioma();
  const [enviando, setEnviando] = useState(false);
  const cambiar = (campo: string, valor: string) => setDatos((d) => ({ ...d, [campo]: valor }));

  return (
    <Modal
      titulo={gasto ? t("ga.d.editar") : t("ga.d.nuevo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || datos.concepto.trim().length < 2 || !(Number(datos.importeEuros) > 0)}
            onClick={async () => {
              setEnviando(true);
              try {
                const cuerpo = {
                  fecha: datos.fecha,
                  concepto: datos.concepto,
                  categoria: datos.categoria,
                  importeEuros: Number(datos.importeEuros),
                  proveedor: datos.proveedor || null,
                  metodoPago: datos.metodoPago,
                  notas: datos.notas || null,
                };
                if (gasto) await api(`/gastos/${gasto.id}`, { metodo: "PATCH", cuerpo });
                else await api("/gastos", { metodo: "POST", cuerpo });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("ga.d.fallo"));
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("g.guardar")}
          </button>
        </>
      }
    >
      <Campo etiqueta={t("ga.d.concepto")}>
        <input
          value={datos.concepto}
          onChange={(e) => cambiar("concepto", e.target.value)}
          autoFocus
          placeholder={t("ga.d.conceptoEjemplo")}
        />
      </Campo>

      <div className="linea-campos">
        <Campo etiqueta={t("ga.d.importe")}>
          <input
            type="number"
            step="0.01"
            min={0}
            value={datos.importeEuros}
            onChange={(e) => cambiar("importeEuros", e.target.value)}
          />
        </Campo>
        <Campo etiqueta={t("ga.d.fecha")}>
          <input type="date" value={datos.fecha} onChange={(e) => cambiar("fecha", e.target.value)} />
        </Campo>
      </div>

      <div className="linea-campos">
        <Campo etiqueta={t("ga.d.categoria")}>
          <select value={datos.categoria} onChange={(e) => cambiar("categoria", e.target.value)}>
            {categorias.map((c) => (
              <option key={c.clave} value={c.clave}>
                {c.nombre}
              </option>
            ))}
          </select>
        </Campo>
        <Campo
          etiqueta={t("ga.d.formaDePago")}
          ayuda={datos.metodoPago === "EFECTIVO" && !gasto ? t("ga.d.saldraDeCaja") : undefined}
        >
          <select value={datos.metodoPago} onChange={(e) => cambiar("metodoPago", e.target.value)}>
            {METODOS.map((m) => (
              <option key={m} value={m}>
                {t(`pago.${m}` as Clave)}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      <Campo etiqueta={t("ga.d.proveedor")}>
        <input value={datos.proveedor} onChange={(e) => cambiar("proveedor", e.target.value)} />
      </Campo>

      <Campo etiqueta={t("ga.d.notas")}>
        <textarea rows={2} value={datos.notas} onChange={(e) => cambiar("notas", e.target.value)} />
      </Campo>

      {gasto && (
        <p className="suave pequeno" style={{ marginBottom: 0 }}>
          {t("ga.d.editarNota")}
        </p>
      )}
    </Modal>
  );
}

/**
 * Anular un gasto.
 *
 * No se borra: si desapareciera, el total del mes bajaría y el club no tendría
 * forma de explicar el hueco. Se queda tachado, con el motivo, y el dinero
 * vuelve al cajón si de allí salió.
 */
function DialogoAnular({
  gasto,
  alCerrar,
  alHecho,
  alFallar,
}: {
  gasto: Gasto;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("ga.an.titulo", { numero: gasto.numero })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton peligro"
            disabled={enviando}
            onClick={async () => {
              setEnviando(true);
              try {
                await api(`/gastos/${gasto.id}`, { metodo: "DELETE", cuerpo: { motivo: motivo || null } });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("ga.an.fallo"));
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("ga.anular")}
          </button>
        </>
      }
    >
      <p>
        <strong>{gasto.concepto}</strong> · {creditos(gasto.importeCentimos)}
      </p>
      <Campo etiqueta={t("ga.an.motivo")} ayuda={t("ga.an.motivoAyuda")}>
        <input
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoFocus
          placeholder={t("ga.an.motivoEjemplo")}
          maxLength={200}
        />
      </Campo>
      <p className="suave pequeno" style={{ marginBottom: 0 }}>
        {t("ga.an.noSeBorra")}
        {gasto.metodoPago === "EFECTIVO" && t("ga.an.vuelveALaCaja")}
        {gasto.compraNumero !== null && t("ga.an.compraNoPagada", { numero: gasto.compraNumero })}
      </p>
    </Modal>
  );
}

/**
 * Las categorías del club.
 *
 * Con la lista fija de antes, todo lo que no encajaba caía en «Otros». Una
 * categoría «Otros» que se lleva la mitad del dinero no dice nada al cerrar el
 * año, así que cada club se hace la suya: la luz aparte del agua, las nóminas
 * aparte del resto.
 */
function DialogoCategorias({ alCerrar, alCambiar }: { alCerrar: () => void; alCambiar: () => void }) {
  const { t, cuenta } = useIdioma();
  const clienteConsultas = useQueryClient();
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const lista = useQuery({
    queryKey: ["categorias-gasto"],
    queryFn: () => api<Categoria[]>("/gastos/categorias"),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ["categorias-gasto"] });
    alCambiar();
  };

  async function hacer(accion: () => Promise<unknown>) {
    setError("");
    try {
      await accion();
      refrescar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("ga.c.fallo"));
    }
  }

  return (
    <Modal
      titulo={t("ga.c.titulo")}
      alCerrar={alCerrar}
      acciones={
        <button className="boton" onClick={alCerrar}>
          {t("ga.c.listo")}
        </button>
      }
    >
      <Aviso tipo="error">{error}</Aviso>

      <div className="fila" style={{ marginBottom: 14 }}>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={t("ga.c.ejemplo")}
          maxLength={40}
          onKeyDown={(e) => e.key === "Enter" && nombre.trim().length >= 2 && void anadir()}
        />
        <button className="boton" disabled={enviando || nombre.trim().length < 2} onClick={() => void anadir()}>
          {t("ga.c.anadir")}
        </button>
      </div>

      {lista.isLoading ? (
        <Cargando />
      ) : (
        <ul className="lista-categorias">
          {(lista.data ?? []).map((c) => (
            <li key={c.id} className={c.activa ? undefined : "apagada"}>
              <div>
                <strong>{c.nombre}</strong>
                <div className="suave pequeno">
                  {c.activa ? cuenta(c.gastos ?? 0, "ga.c.unGasto", "ga.c.gastos") : t("ga.c.apagadaNota")}
                </div>
              </div>
              <div className="fila" style={{ gap: 2 }}>
                {c.activa ? (
                  <button
                    className="boton fantasma"
                    title={(c.gastos ?? 0) > 0 ? t("ga.c.seApagara") : t("ga.c.quitarla")}
                    onClick={() => void hacer(() => api(`/gastos/categorias/${c.id}`, { metodo: "DELETE" }))}
                  >
                    {t("ga.c.quitar")}
                  </button>
                ) : (
                  <button
                    className="boton fantasma"
                    onClick={() =>
                      void hacer(() =>
                        api(`/gastos/categorias/${c.id}`, { metodo: "PATCH", cuerpo: { activa: true } }),
                      )
                    }
                  >
                    {t("ga.c.volverAUsar")}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="suave pequeno" style={{ marginBottom: 0 }}>
        {t("ga.c.nota")}
      </p>
    </Modal>
  );

  async function anadir() {
    setEnviando(true);
    await hacer(async () => {
      await api("/gastos/categorias", { metodo: "POST", cuerpo: { nombre } });
      setNombre("");
    });
    setEnviando(false);
  }
}
